import { shownIds } from "./conditions";
import { isPossiblePhoneNumber } from "react-phone-number-input/min";
import { ApiError } from "../api/client";
import { MULTIPLE, chosen, type Field } from "./forms";

/**
 * LES CHAMPS DONT LA RÉPONSE EST UN CHOIX, et non une saisie — ils ont leur propre mot
 * dans le produit (« Ce choix est obligatoire ») et leur propre forme de vide.
 */
const WITH_OPTIONS = new Set([...MULTIPLE, "radio", "select"]);

/**
 * CE QU'UNE RÉPONSE NE PEUT PAS VALOIR — et ce que le serveur en dit quand il a le
 * dernier mot.
 *
 * ⚠️ CE FICHIER EXISTE POUR ÊTRE TESTÉ. Ses règles prétendent reproduire celles du
 * serveur, à qui l'on ne peut pas demander son avis avant d'envoyer ; une prétention
 * pareille se vérifie, sinon elle dérive en silence et le participant se retrouve
 * devant un « Réessayez » qui ne mène nulle part. Voir `checks.test.ts`, dont les cas
 * sont ceux mesurés sur `cleanEmail` / `mailchecker` de l'API.
 *
 * L'écran, lui, ne fait que placer le reproche sous le bon champ : voir
 * `routes/Informations.tsx`.
 */

/** Le champ fautif et ce qu'on lui reproche — dit sous lui, jamais en tête. */
export type Complaint = { id: string; message: string };

/**
 * LES DEUX MOTS DU CHAMP LAISSÉ VIDE — nommés, parce qu'ils se relisent ailleurs.
 *
 * La phrase posée près du bouton distingue « il manque une réponse » de « la réponse ne
 * peut pas exister », et c'est le seul signal qui les sépare. Reconnu sur la chaîne
 * écrite en dur, il aurait cessé de fonctionner en silence à la première virgule
 * corrigée ; la constante ne peut pas dériver de sa propre valeur.
 */
export const REQUIRED = "Ce champ est obligatoire.";
export const CHOICE_REQUIRED = "Ce choix est obligatoire.";

/**
 * ── L'ADRESSE EMAIL ──
 *
 * ⚠️ ELLE SE RECONNAÎT À SA CLÉ, PAS À SON COMPOSANT. L'API n'a pas de composant
 * « email » : elle envoie un `input`, comme pour un nom de société. La V1 ET le back
 * office la reconnaissent tous deux sur `model.key === 'email'` — c'est la règle du
 * produit, pas une convention inventée ici.
 *
 * Non `extended` : seul le vrai `U.email` part vers les `Authentications` du produit
 * (identifiant de connexion, destinataire des envois). Une question personnalisée qui
 * s'appellerait « email » dort dans le sac `extended` et ne subit rien de tout ça.
 */
export const isEmail = (field: Field) =>
  field.component === "input" && !field.extended && field.key === "email";

/**
 * ── LE MOT DE PASSE ──
 *
 * ⚠️ CE CHAMP CHANGE LE MOT DE PASSE DU COMPTE. Même règle que l'email : l'API n'a pas
 * de composant « password », elle envoie un `input`, et c'est la CLÉ qui le désigne.
 * `forms/updateData` la traite à part — `md5(U.password)` puis un `UPDATE` de
 * `Authentications.password` : ce qu'on tape ici est l'identifiant de connexion, pas une
 * réponse de formulaire.
 *
 * Rendu en `type="text"`, il s'affichait EN CLAIR à l'écran, sans proposition du
 * gestionnaire de mots de passe. Ce n'est pas un cas de figure : 68 formulaires du
 * produit portent cette clé, tous libellés « Nouveau mot de passe ».
 *
 * Non `extended`, pour la même raison que l'email : une question personnalisée qui
 * s'appellerait « password » dort dans le sac `extended` et ne touche à aucun compte.
 */
export const isPassword = (field: Field) =>
  field.component === "input" && !field.extended && field.key === "password";

/**
 * ⚠️ CE MOTIF EST CELUI DU SERVEUR, PAS UN « À PEU PRÈS ». Il vaut d'être lu, parce
 * qu'il est plus strict que le `type="email"` du navigateur ET que la règle de la V1,
 * et que les deux écarts sont volontaires :
 *
 * • UN VRAI DOMAINE, avec un point et une extension. Le navigateur accepte « a@b » ;
 *   `mailchecker`, côté serveur, le refuse. Sans ce point, la saisie passait la
 *   validation native, partait, et revenait en « Réessayez ».
 *
 * • RIEN QUE LES CARACTÈRES QUE LE SERVEUR GARDE. `cleanEmail` ne refuse pas ce qui
 *   sort de `[a-z0-9@._+-]` : il le SUPPRIME, puis enregistre. « prénom@x.fr » devient
 *   « prnom@x.fr » et « o'brien@x.fr » devient « obrien@x.fr » — SANS ERREUR, sous une
 *   adresse que le participant n'a jamais tapée et où il ne recevra donc rien. Une
 *   apostrophe dans un nom français, c'est tout ce qu'il faut. Le refuser ici est le
 *   seul endroit où quelqu'un peut encore le corriger.
 *
 * Ce qu'il laisse passer et que le serveur refusera quand même : les domaines de sa
 * liste noire, qu'on ne peut pas connaître d'ici (voir `refusDeLEmail`).
 */
const EMAIL =
  /^[a-z0-9_+-]+(\.[a-z0-9_+-]+)*@([a-z0-9]+(-[a-z0-9]+)*\.)+[a-z]{2,}$/i;

/** Un caractère sur 4 octets en UTF-8 (U+10000 et au-delà) : 🌻, la plupart des émojis. */
const FOUR_BYTES = /[\u{10000}-\u{10FFFF}]/u;

/** Vrai si le texte ne tiendrait pas dans une colonne native : voir plus bas, dans `check`. */
export const hasEmoji = (text: string) => FOUR_BYTES.test(text);

export const NO_EMOJI = "Les émojis ne sont pas acceptés dans ce champ.";

/**
 * LE REPROCHE À DIRE SOUS UN CHAMP, ou `null` si la réponse peut partir.
 *
 * ⚠️ VIDE N'EST JAMAIS FAUTIF : c'est le travail de `required`, et un champ facultatif
 * se laisse en blanc. Confondre les deux, c'est reprocher son format à une case que
 * personne n'était tenu de remplir.
 */
export function check(field: Field, value: unknown): string | null {
  /**
   * ═══ OBLIGATOIRE ET VIDE — le premier reproche, avant tout contrôle de format ═══
   *
   * ⚠️ IL MANQUAIT, ET IL MANQUAIT SURTOUT AUX CHAMPS À OPTIONS. Les autres portent un
   * `required` que le navigateur fait respecter ; un groupe de cases ou de boutons radio
   * n'en porte pas — rien ne l'arrêtait, et un champ étendu obligatoire se franchissait
   * en n'y touchant pas. Le serveur avait pourtant écrit le cas noir sur blanc : « un
   * objet vide passerait pour une réponse fournie et laisserait un champ obligatoire
   * vide franchir la validation » (voir `selection` dans `funnel/forms.ts`).
   *
   * ⚠️ « VIDE » N'A PAS LA MÊME FORME SELON LE CHAMP, d'où `choisies` plutôt qu'un test
   * de vérité. Une réponse à options est un OBJET `{ clé: true }` — et `{}` est
   * *truthy*, donc « il y a quelque chose » aurait laissé passer précisément ce que le
   * serveur redoute. Le texte, lui, se juge sur sa version élaguée : trois espaces ne
   * sont pas une réponse.
   */
  if (field.required) {
    const filled =
      typeof value === "string" ? value.trim() !== "" : chosen(value).length > 0;
    if (!filled)
      // Le vocabulaire de la V1, qui distingue les deux (`screens/steps/Articles.jsx`
      // pour le choix, `FormBuilder.jsx` pour le champ).
      return WITH_OPTIONS.has(field.component) ? CHOICE_REQUIRED : REQUIRED;
  }

  const typed = typeof value === "string" ? value.trim() : "";
  if (typed === "") return null;

  /**
   * ⚠️ UN NUMÉRO IMPOSSIBLE EST ARRÊTÉ ICI. `required` ne voit qu'un champ vide ;
   * « 06 12 » le remplit et partait en base « +33612 » — dans le champ d'où le
   * produit envoie ses SMS, sans que personne ne l'apprenne avant l'envoi.
   *
   * `isPossiblePhoneNumber` et non `isValid` : la première ne vérifie que la longueur
   * pour l'indicatif, la seconde exige une tranche de numéros connue et refuse donc
   * celles attribuées depuis la dernière version du paquet. Refuser un vrai numéro à
   * l'inscription coûte plus cher qu'en accepter un faux.
   */
  if (field.component === "phone-number")
    return isPossiblePhoneNumber(typed)
      ? null
      : "Ce numéro ne peut pas exister. Vérifiez le pays et le numéro.";

  // Le mot est celui de la V1 et du back office, à la virgule près : le même reproche
  // sur le même champ ne doit pas changer de langue d'un écran du produit à l'autre.
  // 254 est la longueur maximale d'une adresse, et celle que le serveur applique.
  if (isEmail(field))
    return EMAIL.test(typed) && typed.length <= 254
      ? null
      : "Le format de l'email est incorrect.";

  /**
   * ⚠️ UN ÉMOJI NE TIENT PAS DANS UNE COLONNE NATIVE (#497). Nom, ville, adresse, nom de
   * société : ces colonnes sont restées en utf8mb3, et la prod, en `sql_mode` laxiste, y
   * écrivait « ? » à la place de 🌻, sans erreur. Le participant lisait « enregistré »
   * puis retrouvait « Saint-Rémy ? ». Décision de William : on refuse à la saisie.
   *
   * Le serveur refuse aussi (`helpers/fourByteChars` d'apix et d'api-v1, `EMOJI_NOT_ALLOWED`) ; on
   * le dit ICI, sous le champ, parce que son refus ne revenait qu'en « Réessayez ».
   * Un champ `extended` vit dans une colonne JSON qui garde l'émoji : il n'est pas visé,
   * ni le mot de passe, dont seule l'empreinte s'écrit.
   */
  if (!field.extended && !isPassword(field) && hasEmoji(typed)) return NO_EMOJI;

  return null;
}

/** Un champ qui empêche l'envoi, et ce qu'on lui reproche. */
export type Invalid = { field: Field; complaint: Complaint };

/**
 * ═══ TOUS LES CHAMPS QUI EMPÊCHENT L'ENVOI ═══ dans l'ordre du formulaire, vide si tout
 * peut partir.
 *
 * ⚠️ TOUS D'UN COUP, ET C'EST UN CHOIX (tâche #199). Ne rendre que le premier obligeait
 * à cliquer autant de fois sur « Continuer » qu'il y avait d'oublis, pour les découvrir
 * l'un après l'autre. La crainte qui avait fait garder le premier seul (« deux reproches
 * d'un coup, et on ne sait plus lequel le focus désigne ») se lève par l'ordre : le focus
 * va TOUJOURS au premier de la liste, chacun des autres est écrit sous son propre champ,
 * et la phrase du bas les nomme tous. La règle vivait recopiée dans les trois écrans qui
 * servent un formulaire du back office (informations, société, accompagnants) ; elle se
 * lit maintenant une fois.
 *
 * ⚠️ LA VALEUR SE DEMANDE, ELLE NE SE LIT PAS DANS UN SAC. Les trois écrans ne rangent
 * pas leurs réponses de la même façon : deux tiennent un dictionnaire par identifiant, le
 * sous-parcours des accompagnants calcule la sienne (saisie du moment, sinon existant).
 * Leur imposer une forme commune aurait obligé le troisième à en fabriquer une, donc à
 * valider autre chose que ce qu'il écrit.
 */
export function allInvalid(
  fields: ReadonlyArray<Field>,
  value: (field: Field) => unknown,
  /** Ce que le back office a figé : sans réponse à donner, rien à reprocher. */
  locked?: ReadonlySet<string>,
): Invalid[] {
  /* ⚠️ UN CHAMP CACHÉ NE SE REPROCHE PAS : obligatoire mais replié par sa condition,
     il bloquait l'envoi sur un champ que personne ne pouvait voir ni remplir. */
  const shown = shownIds(fields, value);
  const found: Invalid[] = [];
  for (const field of fields) {
    if (locked?.has(field.id) || !shown.has(field.id)) continue;
    const message = check(field, value(field));
    if (message) found.push({ field, complaint: { id: field.id, message } });
  }
  return found;
}

/**
 * LA PHRASE POSÉE PRÈS DU BOUTON, quand la saisie ne peut pas encore partir : une ou
 * deux phrases, chacune avec la clé du dictionnaire qui la dit et les libellés qui
 * remplissent son trou.
 *
 * ⚠️ ELLE EXISTE PARCE QUE LE BOUTON RESTE CLIQUABLE, et c'est un choix. Le griser aurait
 * dit « non » sans dire quoi : sur un formulaire de vingt champs, la seule chose qui
 * désigne les fautifs est justement le clic, qui écrit le reproche sous chacun et amène
 * le focus sur le premier. Un bouton `disabled` sort en plus de l'ordre de tabulation,
 * donc il ne dit rien du tout à qui ne voit pas l'écran. La phrase donne le même signal
 * que le grisé, sans le cul-de-sac.
 *
 * ⚠️ ET ELLE NE S'AFFICHE QU'APRÈS UN PREMIER REFUS, côté écran : posée à l'arrivée sur
 * un formulaire vierge, elle reprocherait de ne pas avoir rempli ce qu'on vient d'ouvrir.
 *
 * ⚠️ REMPLIR N'EST PAS CORRIGER. « Il reste à remplir » devant un numéro déjà saisi mais
 * impossible envoie chercher un champ vide qui n'existe pas. Les deux sortes d'oubli ont
 * donc chacune leur phrase, celle des vides d'abord.
 *
 * ⚠️ LA LISTE EST CELLE DE LA LANGUE LUE (`Intl.ListFormat`) : « A, B et C » en français,
 * « A, B, and C » en anglais, « A、B和C » en chinois. Une virgule écrite en dur aurait
 * figé la ponctuation française dans toutes les langues ; une conjonction écrite en dur,
 * le mot lui-même.
 *
 * ⚠️ UNE QUESTION FINIT DÉJÀ SA PHRASE : derrière « Vous voulez venir ? », le point
 * donnait « venir ?. ». D'où une seconde paire de clés, sans point, plutôt qu'un point
 * retiré après coup : chaque langue garde sa ponctuation à elle (le chinois finit par
 * « 。 »), et une traduction semée ne peut pas remettre le point qu'on a ôté. C'est la
 * liste écrite qui se juge, donc son dernier libellé.
 */
export function leftToDo(
  remaining: ReadonlyArray<{ complaint: Complaint; label: string }>,
  locale = "fr",
): Array<{ key: string; raw: string; names: string }> {
  const isEmpty = ({ complaint }: { complaint: Complaint }) =>
    complaint.message === REQUIRED || complaint.message === CHOICE_REQUIRED;
  const sentences: Array<{ key: string; raw: string; names: string }> = [];

  const toFill = remaining.filter(isEmpty);
  if (toFill.length > 0) {
    const names = listOf(toFill.map((r) => r.label.trim()), locale);
    sentences.push(
      ENDS_A_SENTENCE.test(names)
        ? { key: "LEFT_TO_FILL_ENDED", raw: "Il reste à remplir : $1", names }
        : { key: "LEFT_TO_FILL", raw: "Il reste à remplir : $1.", names },
    );
  }

  const toFix = remaining.filter((r) => !isEmpty(r));
  if (toFix.length > 0) {
    const names = listOf(toFix.map((r) => r.label.trim()), locale);
    sentences.push(
      ENDS_A_SENTENCE.test(names)
        ? { key: "LEFT_TO_FIX_ENDED", raw: "Il reste à corriger : $1", names }
        : { key: "LEFT_TO_FIX", raw: "Il reste à corriger : $1.", names },
    );
  }

  return sentences;
}

/**
 * Des libellés en liste, dans la langue lue. La langue vient de la base (« FR »,
 * « EN », etc.) : `Intl` lève sur une étiquette qu'il ne sait pas lire, on retombe
 * alors sur celle du navigateur, comme `longDay` et `euros`.
 */
function listOf(labels: string[], locale: string): string {
  const options: Intl.ListFormatOptions = { type: "conjunction" };
  try {
    return new Intl.ListFormat(locale, options).format(labels);
  } catch {
    return new Intl.ListFormat(undefined, options).format(labels);
  }
}

/** Un libellé qui porte déjà sa ponctuation finale, chasse pleine comprise (chinois). */
const ENDS_A_SENTENCE = /[?!.:？！。：]$/;

/**
 * ═══ LES REPROCHES, ET LA CLÉ DU DICTIONNAIRE QUI LES DIT ═══
 *
 * ⚠️ CE MODULE EST PUR, DONC IL NE TRADUIT PAS : il n'a ni hook ni langue, et c'est ce
 * qui le rend testable. Il déclare la correspondance ; l'écran qui AFFICHE le message la
 * lit (`funnel/Champ.tsx`, et les trois écrans qui portent un formulaire).
 *
 * ⚠️ INDEXÉE PAR LE MESSAGE, faute de mieux, et c'est assumé : `verifie` rend une phrase,
 * pas un code, parce que ses tests comparent des phrases lisibles depuis le début. Ce que
 * la table ne connaît pas — un refus venu du SERVEUR, déjà français — retombe sur
 * lui-même et s'affiche tel quel, ce qui est exactement ce qu'on veut.
 */
export const COMPLAINTS: Record<string, string> = {
  [REQUIRED]: "FIELD_IS_REQUIRED",
  [CHOICE_REQUIRED]: "CHOICE_IS_REQUIRED",
  "Ce numéro ne peut pas exister. Vérifiez le pays et le numéro.":
    "PHONE_CANNOT_EXIST",
  "Le format de l'email est incorrect.": "EMAIL_FORMAT_INCORRECT",
  [NO_EMOJI]: "EMOJI_NOT_ALLOWED",
  "Cette adresse est déjà celle d'un autre participant. Vérifiez-la, ou utilisez-en une autre.":
    "EMAIL_ALREADY_TAKEN",
  "Cette adresse n'est pas acceptée : vérifiez le domaine (les adresses jetables sont refusées).":
    "EMAIL_DOMAIN_REFUSED",
};

/**
 * LE REFUS DE L'EMAIL PAR LE SERVEUR, EN FRANÇAIS — ou `null` si la panne est d'un
 * autre ordre.
 *
 * ⚠️ CES DEUX-LÀ NE SONT PAS DES PANNES, et « Réessayez » est un mauvais conseil : la
 * même adresse renvoyée donnera exactement le même refus, autant de fois qu'on la
 * renvoie. Ils se corrigent dans le champ, donc ils se disent sous le champ.
 *
 * ⚠️ ILS ARRIVENT EN 400 NU, sans code exploitable : le serveur répond `{ message }`,
 * où le message est tantôt une phrase française (`cleanEmail`), tantôt un identifiant
 * brut (`EMAIL_NOT_AVAILABLE`) qu'on ne montre à personne. On lit donc le message — et
 * à défaut de le reconnaître, on rend la main au bandeau générique plutôt que
 * d'inventer une cause.
 */
export function emailRefusal(error: unknown): string | null {
  if (!(error instanceof ApiError)) return null;

  if (error.message.includes("EMAIL_NOT_AVAILABLE"))
    return "Cette adresse est déjà celle d'un autre participant. Vérifiez-la, ou utilisez-en une autre.";

  /**
   * Le format, alors que `EMAIL` vient de l'accepter : il ne reste que la liste noire
   * de `mailchecker` — adresses jetables et domaines sosies de `gmail.com`. Cent mille
   * entrées qu'on n'embarque pas dans le paquet pour ce seul écran ; le serveur, lui,
   * les a. D'où un mot qui désigne le domaine sans jurer de la raison exacte.
   */
  if (/format de l['’]email/i.test(error.message))
    return "Cette adresse n'est pas acceptée : vérifiez le domaine (les adresses jetables sont refusées).";

  return null;
}
